import { useCallback, useMemo } from 'react';
import { MeasurementToolState } from '@/types';
import * as moduleState from '../state';
import { editPointOfInterest as editPointOfInterestState } from '@/components/Analysis/modules/pointOfInterest/state';
import { InputAdormentIconTypeEnum, Maybe, PoiLineMeasurement } from '@/__generated__/graphql';
import {
  calcMeasurementLinesDistance,
  createEmptyMeasurementProps,
  poiLineMeasurmentToProps,
} from './utils';
import { MeasurementLinesProps, Number3 } from '@abyss/3d-viewer';
import { DEFAULT_MEASURMENT_LINES_STYLE } from '../constants';
import { useAtom, useAtomValue } from 'jotai';

export const useMeasurementTool = () => {
  const [measurementsToAdd, setMeasurementsToAdd] = useAtom(moduleState.measurementsToAdd);
  const [measurementDistanceWithNoBoundry, setMeasurementLineDistance] = useAtom(
    moduleState.measurementLineDistance
  );
  const [measurementToolState, setMeasurementToolState] = useAtom(
    moduleState.measurementToolState
  );
  const [activeFieldId, setActiveFieldId] = useAtom(moduleState.activeMeasurementFieldId);
  const [measurementsMap, setMeasurementsMap] = useAtom(moduleState.measurementsByField);
  const editPointOfInterest = useAtomValue(editPointOfInterestState);
  const poi = editPointOfInterest?.pointOfInterest;

  const measurements: MeasurementLinesProps | undefined =
    activeFieldId &&
    measurementsMap &&
    Array.isArray(measurementsMap[activeFieldId]?.points) &&
    measurementsMap[activeFieldId].points.length >= 2
      ? measurementsMap[activeFieldId]
      : measurementsToAdd;

  const isMeasurementMenuVisible = useMemo(() => {
    return measurementToolState === MeasurementToolState.WAITING_FOR_POINT;
  }, [measurementToolState]);

  const activeMeasurementLength = useMemo(() => {
    if (!activeFieldId) {
      return measurementDistanceWithNoBoundry;
    }

    if (!measurements || !Array.isArray(measurements.points) || measurements.points.length < 2) {
      return 0;
    }

    return calcMeasurementLinesDistance(measurements.points);
  }, [activeFieldId, measurementDistanceWithNoBoundry, measurements]);

  const measurementDistance = useMemo(() => {
    return activeMeasurementLength.toFixed(2);
  }, [activeMeasurementLength]);

  const measurementToolHandlerForAdormants = useCallback(() => {
    setMeasurementToolState(MeasurementToolState.WAITING_FOR_POINT);
  }, [setMeasurementToolState]);

  const defaultReplayHandler = useCallback(() => {}, []);

  const inputAdormantHandlerMap = useMemo(() => {
    return {
      [InputAdormentIconTypeEnum.MeasurementTool]: measurementToolHandlerForAdormants,
      [InputAdormentIconTypeEnum.Reload]: defaultReplayHandler,
      '': () => {},
    };
  }, [measurementToolHandlerForAdormants, defaultReplayHandler]);

  const setLineDistance = useCallback(
    (distance: number) => {
      setMeasurementLineDistance(distance);
    },
    [setMeasurementLineDistance]
  );

  const handleMeasurementsOnPointSelect = useCallback(
    (position: Number3) => {
      if (measurementToolState !== MeasurementToolState.WAITING_FOR_POINT) {
        return;
      }

      if (!activeFieldId) {
        // if measurement tool is enabled, calculate position and pass it to abyss3dViewer
        setMeasurementsToAdd((oldMeasurements) => {
          return {
            styles: oldMeasurements?.style || DEFAULT_MEASURMENT_LINES_STYLE,
            points: [...(oldMeasurements?.points || []), position],
            setLineDistance: oldMeasurements?.setLineDistance || setLineDistance,
          };
        });

        return;
      }

      setMeasurementsMap((previous = {}) => {
        const current = previous[activeFieldId] || createEmptyMeasurementProps(setLineDistance);
        const existingDBPoints = poi?.lineMeasurement?.points?.filter(
          (point) => point?.templateFieldId === activeFieldId
        );

        const isFirstClickAfterEdit =
          current.points?.length &&
          existingDBPoints?.length &&
          current.points.length === existingDBPoints.length;

        const base = isFirstClickAfterEdit ? createEmptyMeasurementProps(setLineDistance) : current;

        return {
          ...previous,
          [activeFieldId]: {
            ...base,
            points: [...(base.points || []), position],
          },
        };
      });
    },
    [
      activeFieldId,
      measurementToolState,
      poi?.lineMeasurement?.points,
      setLineDistance,
      setMeasurementsMap,
      setMeasurementsToAdd,
    ]
  );

  const populateMeasurementFromPOI = useCallback(
    (measurement: Maybe<PoiLineMeasurement> | undefined) => {
      if (!measurement) return;
      const fieldMeasurements = poiLineMeasurmentToProps(measurement);
      setMeasurementsMap(fieldMeasurements);
    },
    [setMeasurementsMap]
  );

  const cancelMeasuring = useCallback(() => {
    setMeasurementToolState(MeasurementToolState.IN_ACTIVE);
    setMeasurementsMap(undefined);
    setMeasurementsToAdd(undefined);
    setActiveFieldId(undefined);
  }, [setActiveFieldId, setMeasurementToolState, setMeasurementsMap, setMeasurementsToAdd]);

  const doneMeasuring = useCallback(() => {
    setMeasurementToolState(MeasurementToolState.DONE);
    setActiveFieldId(undefined);
  }, [setActiveFieldId, setMeasurementToolState]);

  const allMeasurements = measurementsMap ?? {};

  return {
    isMeasurementMenuVisible,
    measurementToolState,
    measurementDistance,
    allMeasurements,
    measurements,
    cancelMeasuring,
    doneMeasuring,
    handleMeasurementsOnPointSelect,
    measurementToolHandlerForAdormants,
    inputAdormantHandlerMap,
    populateMeasurementFromPOI,
  };
};
